import axios from 'axios';

const configuredBackend = (process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_BASE_URL || '').trim();
let normalizedBackend = configuredBackend.replace(/\/+$/, '');
// Accept env values that already include /api and prevent /api/api requests.
normalizedBackend = normalizedBackend.replace(/\/api$/i, '');
// Make host-only values usable (e.g. my-api.onrender.com).
if (normalizedBackend && !/^https?:\/\//i.test(normalizedBackend)) {
  const host = normalizedBackend.replace(/^\/+/, '');
  const protocol = host.startsWith('localhost') || host.startsWith('127.0.0.1') ? 'http' : 'https';
  normalizedBackend = `${protocol}://${host}`;
}
// If no env var is set, default to same-origin /api instead of undefined/api.
const API = normalizedBackend ? `${normalizedBackend}/api` : '/api';

// Token storage key
const TOKEN_KEY = 'pkec_auth_token';
const REFRESH_TOKEN_KEY = 'pkec_refresh_token';
const USE_LEGACY_LOCAL_TOKEN = String(process.env.REACT_APP_AUTH_LOCAL_TOKEN_FALLBACK || '').toLowerCase() === 'true';
const USE_SESSION_TOKEN_CACHE = String(process.env.REACT_APP_AUTH_SESSION_STORAGE || 'true').toLowerCase() === 'true';
const USE_BEARER_AUTH_FALLBACK = USE_LEGACY_LOCAL_TOKEN || USE_SESSION_TOKEN_CACHE;

const safeLocalStorage = () => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch (_err) {
    return null;
  }
};

const safeSessionStorage = () => {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch (_err) {
    return null;
  }
};

const readStoredToken = (key) => {
  if (USE_SESSION_TOKEN_CACHE) {
    const value = safeSessionStorage()?.getItem(key) || '';
    if (value) return value;
  }
  if (USE_LEGACY_LOCAL_TOKEN) {
    return safeLocalStorage()?.getItem(key) || '';
  }
  return '';
};

const writeStoredToken = (key, value) => {
  const tokenValue = String(value || '').trim();
  if (USE_SESSION_TOKEN_CACHE) {
    const session = safeSessionStorage();
    if (session) {
      if (tokenValue) session.setItem(key, tokenValue);
      else session.removeItem(key);
    }
  }
  if (USE_LEGACY_LOCAL_TOKEN) {
    const local = safeLocalStorage();
    if (local) {
      if (tokenValue) local.setItem(key, tokenValue);
      else local.removeItem(key);
    }
  }
};

const purgeLegacyStoredTokens = () => {
  if (!USE_SESSION_TOKEN_CACHE) {
    safeSessionStorage()?.removeItem(TOKEN_KEY);
    safeSessionStorage()?.removeItem(REFRESH_TOKEN_KEY);
  }
  if (!USE_LEGACY_LOCAL_TOKEN) {
    safeLocalStorage()?.removeItem(TOKEN_KEY);
    safeLocalStorage()?.removeItem(REFRESH_TOKEN_KEY);
  }
};

purgeLegacyStoredTokens();

let inMemoryAccessToken = readStoredToken(TOKEN_KEY);
let inMemoryRefreshToken = readStoredToken(REFRESH_TOKEN_KEY);
let lastRefreshFailureAt = 0;
let refreshInFlightPromise = null;
const REFRESH_RETRY_COOLDOWN_MS = 30000;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

const authApi = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

const isAuthEndpoint = (url = '') => {
  const target = String(url || '');
  return target.includes('/auth/refresh') || target.includes('/auth/logout');
};

const currentRefreshToken = () =>
  inMemoryRefreshToken || readStoredToken(REFRESH_TOKEN_KEY);

const refreshAccessToken = async () => {
  if (refreshInFlightPromise) {
    return refreshInFlightPromise;
  }
  if (Date.now() - lastRefreshFailureAt < REFRESH_RETRY_COOLDOWN_MS) {
    throw new Error('Refresh cooldown active');
  }

  refreshInFlightPromise = (async () => {
    const refreshToken = currentRefreshToken();
    try {
      const refreshResponse = await authApi.post('/auth/refresh', refreshToken ? { refresh_token: refreshToken } : {});
      const nextToken = String(refreshResponse?.data?.token || '').trim();
      const nextRefreshToken = String(refreshResponse?.data?.refresh_token || '').trim() || refreshToken;
      if (!nextToken && USE_BEARER_AUTH_FALLBACK) {
        throw new Error('Refresh response missing access token');
      }
      setAuthToken(nextToken, nextToken ? nextRefreshToken : '');
      lastRefreshFailureAt = 0;
      return nextToken;
    } catch (refreshError) {
      lastRefreshFailureAt = Date.now();
      const status = Number(refreshError?.response?.status || 0);
      if ([400, 401, 403].includes(status)) {
        clearAuthToken();
      }
      throw refreshError;
    } finally {
      refreshInFlightPromise = null;
    }
  })();

  return refreshInFlightPromise;
};

// Request interceptor to attach auth token to every request
api.interceptors.request.use(
  (config) => {
    const token = inMemoryAccessToken || readStoredToken(TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle 401 errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    const requestUrl = String(originalRequest.url || '');

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint(requestUrl)) {
      originalRequest._retry = true;
      try {
        const nextToken = await refreshAccessToken();
        originalRequest.headers = originalRequest.headers || {};
        if (nextToken) originalRequest.headers.Authorization = `Bearer ${nextToken}`;
        else delete originalRequest.headers.Authorization;
        return api(originalRequest);
      } catch (_refreshError) {
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

// Auth token helpers
export const setAuthToken = (token, refreshToken = '') => {
  inMemoryAccessToken = String(token || '').trim();
  inMemoryRefreshToken = String(refreshToken || '').trim();
  writeStoredToken(TOKEN_KEY, inMemoryAccessToken);
  writeStoredToken(REFRESH_TOKEN_KEY, inMemoryRefreshToken);
};

export const getAuthToken = () => inMemoryAccessToken || readStoredToken(TOKEN_KEY);
export const clearAuthToken = () => {
  inMemoryAccessToken = '';
  inMemoryRefreshToken = '';
  writeStoredToken(TOKEN_KEY, '');
  writeStoredToken(REFRESH_TOKEN_KEY, '');
};

export const getApiBaseUrl = () => {
  const normalizedApi = API.replace(/\/+$/, '');
  if (/^https?:\/\//i.test(normalizedApi)) {
    return normalizedApi;
  }
  if (typeof window === 'undefined') {
    return normalizedApi;
  }
  const withLeadingSlash = normalizedApi.startsWith('/') ? normalizedApi : `/${normalizedApi}`;
  return `${window.location.origin}${withLeadingSlash}`;
};

// Auth
export const getCurrentUser = () => api.get('/auth/me');
export const logout = () => api.post('/auth/logout');

// Dropbox
export const getDropboxAuthUrl = (redirectUri) => 
  api.get('/dropbox/auth-url', { params: { redirect_uri: redirectUri } });
export const dropboxCallback = (code, state, redirectUri) => 
  api.post('/dropbox/callback', null, { params: { code, state, redirect_uri: redirectUri } });
export const listDropboxFiles = (path = '', options = {}) =>
  api.get('/dropbox/files', { params: { path, recursive: options?.recursive ? 'true' : undefined } });
export const downloadDropboxFile = (path) => 
  api.get('/dropbox/download', { params: { path } });
export const renameDropboxFiles = (data) => 
  api.post('/dropbox/rename', data);
export const scanFolder = (folderPath) =>
  api.post('/dropbox/scan-folder', { folder_path: folderPath });
export const batchRename = (files, batchNumber, totalBatches) =>
  api.post('/dropbox/batch-rename', { files, batch_number: batchNumber, total_batches: totalBatches });
export const estimateTaskDuration = (payload) =>
  api.post('/performance/estimate', payload);
export const getOperationalKnobs = () =>
  api.get('/settings/operational-knobs');
export const updateOperationalKnobs = (overrides) =>
  api.put('/settings/operational-knobs', { overrides });
export const resetOperationalKnobs = (keys = null) =>
  api.post('/settings/operational-knobs/reset', keys && Array.isArray(keys) ? { keys } : {});
export const getDropboxPolicy = () =>
  api.get('/settings/dropbox-policy');
export const updateDropboxPolicy = (destructiveRoots) =>
  api.put('/settings/dropbox-policy', { destructive_roots: destructiveRoots });
export const getAIPreferences = () =>
  api.get('/settings/ai-preferences');
export const updateAIPreferences = (prefs) =>
  api.put('/settings/ai-preferences', prefs);
export const getDiscoveryDefaults = () =>
  api.get('/settings/discovery-defaults');
export const updateDiscoveryDefaults = (payload) =>
  api.put('/settings/discovery-defaults', payload);
export const controlDiscoveryParseJob = (jobId) =>
  api.post(`/discovery/parse-pdf/job-control/${jobId}`);

// Exhibit builder
export const startExhibitJob = (payload) =>
  api.post('/exhibits/start', payload);
export const reconcileExhibits = (payload) =>
  api.post('/exhibits/reconcile', payload);
export const buildExhibitNumberList = (payload) =>
  api.post('/exhibits/build-number-list', payload, { responseType: 'blob' });
export const getExhibitJobStatus = (jobId) =>
  api.get(`/exhibits/job-status/${jobId}`);
export const controlExhibitJob = (jobId, action) =>
  api.post(`/exhibits/job-control/${jobId}`, { action });

// Exhibit builder — Brief Hyperlink Mode
export const previewExhibitHyperlinks = (formData) =>
  api.post('/exhibits/hyperlink/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const startExhibitHyperlinkJob = (formData) =>
  api.post('/exhibits/hyperlink/start', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getExhibitHyperlinkJobStatus = (jobId) =>
  api.get(`/exhibits/hyperlink/job-status/${jobId}`);
export const controlExhibitHyperlinkJob = (jobId, action) =>
  api.post(`/exhibits/hyperlink/job-control/${jobId}`, { action });

// Timeline
export const startTimelineJob = (payload) =>
  api.post('/timeline/start', payload);
export const getTimelineJobStatus = (jobId) =>
  api.get(`/timeline/job-status/${jobId}`);
export const controlTimelineJob = (jobId, action = 'cancel') =>
  api.post(`/timeline/job-control/${jobId}`, { action });
export const openTimelineSSE = async (jobId, token, signal) => {
  const base = getApiBaseUrl();
  const response = await fetch(`${base}/timeline/stream/${encodeURIComponent(jobId)}`, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include',
    signal,
  });
  if (!response.ok) {
    throw new Error(`Timeline stream failed (${response.status})`);
  }
  if (!response.body) {
    throw new Error('Timeline stream missing response body');
  }
  return response;
};

export const exportTimelineExcel = (
  events,
  analysis,
  saveToDropbox = false,
  dropboxPath = null,
  filename = null,
  missingDocuments = [],
  projectMilestones = [],
  existingTimelinePath = null,
) =>
  api.post('/timeline/export-excel', {
    events,
    analysis,
    missing_documents: missingDocuments,
    project_milestones: projectMilestones,
    save_to_dropbox: saveToDropbox,
    dropbox_path: dropboxPath,
    filename,
    existing_timeline_path: existingTimelinePath,
  });

export const uploadTimelineCaseDoc = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/timeline/case-doc/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const relinkTimelineExcel = (formData) =>
  api.post('/timeline/relink-excel', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    responseType: 'blob',
  });

// QC runner
export const controlQcJob = (jobId, action) =>
  api.post(`/qc/job-control/${jobId}`, { action });

// QC duplicate resolution from Excel
export const resolveQcDuplicatesFromExcel = (formData) =>
  api.post('/qc/duplicates/resolve', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

// Family Binder
export const startFamilyBinderJob = (formData) =>
  api.post('/family-binder/start', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getFamilyBinderJobStatus = (jobId) =>
  api.get(`/family-binder/job-status/${jobId}`);
export const downloadFamilyBinderLog = (jobId) =>
  api.get(`/family-binder/download/${jobId}`, { responseType: 'blob' });
export const controlFamilyBinderJob = (jobId, action) =>
  api.post(`/family-binder/job-control/${jobId}`, { action });

export default api;
